import '@/styles/ingame-flash.css';
import { useCallback, useEffect, useRef, useState } from 'react';

export type IngameFlashType = 'success' | 'danger' | 'warning' | 'info';

export interface IngameFlashState {
  message: string;
  type: IngameFlashType;
}

interface IngameFlashProps {
  flash: IngameFlashState | null;
}

export function IngameFlash({ flash }: IngameFlashProps) {
  if (!flash) return null;
  return (
    <div className="ingame-flash" data-type={flash.type} role="status">
      {flash.message}
    </div>
  );
}

export function useIngameFlash(durationMs = 3000) {
  const [flash, setFlash] = useState<IngameFlashState | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearFlash = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setFlash(null);
  }, []);

  const showFlash = useCallback(
    (message: string, type: IngameFlashType = 'info') => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      setFlash({ message, type });
      timerRef.current = window.setTimeout(() => {
        setFlash(null);
        timerRef.current = null;
      }, durationMs);
    },
    [durationMs],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  return { flash, showFlash, clearFlash };
}
